import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusClass = { Live: "status-live", MVP: "status-mvp", Idea: "status-idea" };
const categories = ["All", "Infrastructure", "Protocol", "Ecosystem", "Agency", "Wealth", "AI", "AI Finance"];
const statuses = ["All", "Live", "MVP", "Idea"];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }),
};

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const filtered = projects.filter((p) => {
    if (filterCat !== "All" && p.category !== filterCat) return false;
    if (filterStatus !== "All" && p.status !== filterStatus) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.tagline.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <main className="min-h-screen bg-white pt-24 md:pt-28" data-testid="portfolio-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="mb-12">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-3">Portfolio</p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">Our Ventures</h1>
          <p className="font-sans text-base md:text-lg text-slate-500 max-w-2xl leading-relaxed">
            Each venture solves a real problem. Together, they form the infrastructure of programmable finance.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-10 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-slate-200 focus:ring-blue-500 font-sans text-sm"
                data-testid="portfolio-search-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 mr-2">Category:</span>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilterCat(c)}
                className={`px-3 py-1 rounded-full text-xs font-sans transition-colors ${
                  filterCat === c
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
                data-testid={`filter-cat-${c.toLowerCase().replace(/\s/g, '-')}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 mr-2">Status:</span>
            {statuses.map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 rounded-full text-xs font-sans transition-colors ${
                  filterStatus === s
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
                data-testid={`filter-status-${s.toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <motion.div
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filtered.map((project, i) => (
            <motion.div key={project.project_id} variants={fadeUp} custom={i}>
              <Link to={`/project/${project.project_id}`} data-testid={`portfolio-card-${project.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div
                  className="project-card bg-white border border-slate-200 rounded-xl p-6 cursor-pointer h-full"
                  style={{ borderLeftWidth: "3px", borderLeftColor: project.brand_color }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                      {project.category}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${statusClass[project.status] || "status-idea"}`}>
                      {project.status}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-slate-900 mb-2">{project.name}</h3>
                  <p className="font-sans text-sm text-slate-500 leading-relaxed">{project.tagline}</p>
                  {project.url && (
                    <div className="mt-4 flex items-center gap-1 text-xs text-slate-400">
                      <ExternalLink className="w-3 h-3" />
                      <span className="font-mono">{project.url.replace("https://", "")}</span>
                    </div>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="font-sans text-slate-400">No projects match your filters.</p>
          </div>
        )}
      </div>
    </main>
  );
}
